<template>
  <div class="space-y-3">
    <!-- Cash Flow Title -->
    <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
      {{ t('app.views.cashRegister.cashFlowDetails') }}
    </h4>

    <!-- Cash Flow Breakdown -->
    <div class="space-y-2">
      <!-- Initial Balance -->
      <div class="flex justify-between items-center py-2  border-gray-100 dark:border-gray-700">
        <span class="text-sm text-gray-600 dark:text-gray-400">
          {{ t('app.views.cashRegister.initialBalance') }}
        </span>
        <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {{ formatCurrency(initialBalance) }}
        </span>
      </div>

      <!-- Cash Sales -->
      <div class="flex justify-between items-center py-2  border-gray-100 dark:border-gray-700">
        <div class="flex items-center gap-2">
          <span class="text-sm text-green-600 dark:text-green-400">+</span>
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {{ t('app.views.cashRegister.cashSales') }}
          </span>
        </div>
        <span class="text-sm font-semibold text-green-600 dark:text-green-400">
          {{ formatCurrency(cashSales) }}
        </span>
      </div>

      <!-- Expenses -->
      <div class="flex justify-between items-center py-2  border-gray-100 dark:border-gray-700">
        <div class="flex items-center gap-2">
          <span class="text-sm text-red-600 dark:text-red-400">−</span>
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {{ t('app.views.cashRegister.expenses') }}
          </span>
        </div>
        <span class="text-sm font-semibold text-red-600 dark:text-red-400">
          {{ formatCurrency(expenses) }}
        </span>
      </div>

      <!-- Expected Cash Balance (Result) -->
      <div class="flex justify-between items-center pt-3 border-t-2 border-gray-200 dark:border-gray-600">
        <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
          {{ t('app.views.cashRegister.expectedCashBalance') }}
        </span>
        <span 
          class="text-sm font-semibold"
          :class="getExpectedBalanceClass()"
        >
          {{ formatCurrency(expectedBalance) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  initialBalance: number;
  cashSales: number;
  expenses: number;
}>();

// Calculate expected cash balance
const expectedBalance = computed(() => {
  return props.initialBalance + props.cashSales - props.expenses;
});

// Get CSS class for expected balance based on sign
const getExpectedBalanceClass = () => {
  if (expectedBalance.value > 0) {
    return 'text-green-700 dark:text-green-400';
  } else if (expectedBalance.value < 0) {
    return 'text-red-700 dark:text-red-400';
  }
  return 'text-gray-700 dark:text-gray-400';
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(value || 0);
};
</script>
